import { useMemo, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { accountDetailPath } from '../../lib/portal';
import { formatPhoneDisplay } from '../../lib/intlPhone';
import { useCrmData } from '../../contexts/CrmDataContext';
import type { Client, Contact, Project } from '../../types/crm';
import { contactLabel } from '../../utils/crmHelpers';
import { formatSingaporeDate } from '../../utils/dateTime';
import { projectStatusModifier, updatesForProject } from '../../utils/projectHelpers';

function display(value?: string | null): string {
  const v = value?.trim();
  return v || '—';
}

function completedDateFromStatusLog(project: Project, updates: ReturnType<typeof updatesForProject>): string | null {
  if ((project.status ?? '').trim().toLowerCase() !== 'completed') return null;
  for (let i = updates.length - 1; i >= 0; i -= 1) {
    const content = updates[i].content ?? '';
    if (/Status changed to 'Completed'/i.test(content) && updates[i].created_at) {
      return updates[i].created_at!;
    }
  }
  return null;
}

interface SidebarRowProps {
  label: string;
  children: ReactNode;
}

function SidebarRow({ label, children }: SidebarRowProps) {
  return (
    <div className="project-side-dl__row">
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

interface ProjectDetailSidebarProps {
  project: Project;
  client?: Client;
  contact?: Contact;
}

export function ProjectDetailSidebar({ project, client, contact }: ProjectDetailSidebarProps) {
  const { data } = useCrmData();

  const completedRaw = useMemo(() => {
    if ((project.status ?? '').trim().toLowerCase() !== 'completed') return null;

    const fromRow = project.completed_date ?? project.completed_at ?? null;
    if (fromRow) return fromRow;

    const timeline = updatesForProject(data.updates, project.id);
    return completedDateFromStatusLog(project, timeline);
  }, [project, data.updates]);

  return (
    <div className="project-detail-sidebar">
      <section className="project-side-card">
        <h2 className="project-side-card__title">Details</h2>
        <dl className="project-side-dl">
          <SidebarRow label="Status">
            <span className={`project-status ${projectStatusModifier(project.status)}`}>
              {project.status ?? 'Ongoing'}
            </span>
          </SidebarRow>
          <SidebarRow label="Creation date">{formatSingaporeDate(project.created_at)}</SidebarRow>
          <SidebarRow label="Completed date">{formatSingaporeDate(completedRaw)}</SidebarRow>
        </dl>
      </section>

      <section className="project-side-card">
        <h2 className="project-side-card__title">Account details</h2>
        <dl className="project-side-dl">
          <SidebarRow label="Account name">
            {client ? (
              <Link to={accountDetailPath(client.code)} className="project-side-dl__link">
                {client.company_name}
              </Link>
            ) : (
              '—'
            )}
          </SidebarRow>
          <SidebarRow label="Account code">{display(client?.code)}</SidebarRow>
          <SidebarRow label="Main phone">{display(formatPhoneDisplay(client?.main_phone))}</SidebarRow>
          <SidebarRow label="Website">
            {client?.website?.trim() ? (
              <a href={client.website} target="_blank" rel="noreferrer" className="project-side-dl__link">
                {client.website}
              </a>
            ) : (
              '—'
            )}
          </SidebarRow>
          <SidebarRow label="Address">
            <span className="project-side-dl__multiline">{display(client?.address)}</span>
          </SidebarRow>
        </dl>
      </section>

      <section className="project-side-card">
        <h2 className="project-side-card__title">Key contact details</h2>
        {contact ? (
          <dl className="project-side-dl">
            <SidebarRow label="Name">{contactLabel(contact)}</SidebarRow>
            <SidebarRow label="Job title">{display(contact.job_title)}</SidebarRow>
            <SidebarRow label="Email">
              {contact.email?.trim() ? (
                <a href={`mailto:${contact.email}`} className="project-side-dl__link">
                  {contact.email}
                </a>
              ) : (
                '—'
              )}
            </SidebarRow>
            <SidebarRow label="Phone">{display(formatPhoneDisplay(contact.phone))}</SidebarRow>
          </dl>
        ) : (
          <p className="project-side-card__empty">No key contact assigned.</p>
        )}
      </section>
    </div>
  );
}
